import { MatchMakeError, type Client } from "colyseus.js";

/**
 * THE SEAT'S TOKEN NEVER RIDES A SOCKET URL (card-a5 item 2).
 *
 * An online seat keeps ONE reconnection token for the whole match, and a reconnect skips the
 * game-server's `onAuth`: the token is a key to the player's chair, their hand included.
 * colyseus.js's own `client.reconnect(token)` sends it in the matchmake POST's body AND in the socket
 * URL (`?sessionId=…&reconnectionToken=…`), which every URL-logging layer keeps — nginx's error log,
 * Cloudflare — for as long as the match runs.
 *
 * So this client asks for a CONNECT TICKET in the POST (`connectTicket`) and puts the ticket in the
 * URL instead: single-use, bound to the session, dead within seconds (game-server
 * `rooms/connect-ticket.ts`). The token travels only in the POST body and inside the socket.
 * A game-server that mints no ticket answers as before and the token rides the URL as before, so
 * either side can deploy first without breaking a reconnect — but for the LOGS the web goes first
 * (infra/deploy-2026-10-11-card-a5/item-02-nginx/README.md: a one-token game-server ahead of this
 * bundle makes every old bundle's URL a live key). Errors are colyseus.js's own `MatchMakeError`
 * (code + message), so every reader of a refusal — «not yet», «seat gone», «room locked» — reads
 * it unchanged.
 *
 * THE POST ALSO CARRIES THIS LAUNCH'S initData (card-74 round 2, #1). colyseus gives a socket that comes
 * back by token the `auth` of the seat's PREVIOUS socket, so a newer copy of the app that went straight
 * back on the shared ticket was ordered by the OLDER copy's launch — and the older copy's door then took
 * the seat from the copy in front of the player. The game-server binds the launch it validates here to
 * the ticket (`apps/game-server/src/rooms/connect-ticket.ts`). Body only, never the URL. An older
 * game-server ignores the field: colyseus's `reconnect` reads `reconnectionToken` alone and the card-a5
 * mint `connectTicket` alone (proved against the 2712db17 server's own code, card-74 round 2).
 */

/** What the reconnect POST answers: colyseus's seat reservation, plus the ticket when one was minted. */
interface ReconnectAnswer {
  error?: unknown;
  code?: unknown;
  connectTicket?: unknown;
  [key: string]: unknown;
}

/**
 * Make `client.reconnect` ask for a connect ticket, carrying `initData` (this launch's, when there is
 * one). Returns the same client. Leaves it untouched when it lacks colyseus.js's matchmake HTTP or
 * `consumeSeatReservation` (a colyseus.js that moved them): the reconnect then works exactly as before
 * rather than not at all.
 */
export function withConnectTickets<C extends Client>(client: C, initData = ""): C {
  const http = (client as { http?: { post?: unknown } }).http;
  if (typeof http?.post !== "function" || typeof client.consumeSeatReservation !== "function") {
    return client;
  }
  const plain = client.reconnect.bind(client);
  client.reconnect = (async (reconnectionToken: string) => {
    const [roomId, token] = reconnectionToken.split(":");
    if (!roomId || !token) return plain(reconnectionToken); // colyseus.js raises its own format error
    const response = await client.http.post(`matchmake/reconnect/${roomId}`, {
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        reconnectionToken: token,
        connectTicket: 1,
        ...(initData.length > 0 ? { initData } : {}),
      }),
    });
    const answer = response.data as ReconnectAnswer;
    if (answer.error) throw new MatchMakeError(String(answer.error), Number(answer.code));
    const ticket =
      typeof answer.connectTicket === "string" && answer.connectTicket.length > 0
        ? answer.connectTicket
        : token;
    return client.consumeSeatReservation({ ...answer, reconnectionToken: ticket });
  }) as C["reconnect"];
  return client;
}
